import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import './styles/ReviewAndBillingStep.css';
import ScrollToTop from '../common/ScrollToTop';
import AccountStepper from './shared/AccountStepper';
import {
  AccountSetupStepShell,
  AccountSetupStepContainer,
  AccountSetupButtonRow,
  AccountSetupBackButton,
  AccountSetupNextButton,
} from './shared/accountSetupStepStyles';
import { createSubscription } from '../../services/payment/subscriptionService';
import { hasConfirmedFirstPayment } from '../../services/accountSetup/provisionPaidAccount';
import DeluxeSubscriptionCheckout from '../../../deluxe/embedded/subscription/DeluxeSubscriptionCheckout';
import { featureFlags } from '../../config/featureFlags';
import DeluxeEnvironmentToggle from './components/DeluxeEnvironmentToggle';
import { getDeluxeEnvironment, isDeluxeSandbox } from '../../services/payment/deluxeEnvironment';
import AccountSetupLoadingOverlay from './loading/AccountSetupLoadingOverlay';
import { listPackages } from '../../../api/subscriptions/subscriptions';

// Styled Components
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  grid-template-areas: 'form summary';
  gap: ${theme.spacing.xl};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'summary'
      'form';
  }
`;

const FormContainer = styled.form`
  background: transparent;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
  grid-area: form;
  
  @media (max-width: 768px) {
    padding: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;

const OrderSummary = styled('div')`
  padding: ${theme.spacing.xl};
  border-radius: 20px;
  background: var(--login-form-bg);
  border: 1px solid var(--login-form-border);
  height: fit-content;
  position: sticky;
  top: calc(72px + env(safe-area-inset-top, 0px) + ${theme.spacing.md});
  box-shadow: var(--login-form-shadow);
  
  @media (max-width: 900px) {
    position: static;
    margin-top: ${theme.spacing.xl};
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.lg};
    margin-top: ${theme.spacing.lg};
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${theme.spacing.sm} 0;
  color: ${props => props.isTotal ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: ${props => props.isTotal ? 600 : 400};
  font-size: ${props => props.isTotal ? '1.1rem' : '1rem'};
`;

const ReviewCard = styled('div')`
  margin-bottom: ${theme.spacing.xl};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: 480px) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const ReviewSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: 480px) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const ReviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: ${theme.spacing.sm} 0;
  
  & > span:first-of-type {
    color: ${theme.colors.text.secondary};
  }
  
  & > span:last-of-type {
    color: ${theme.colors.text.primary};
    font-weight: 500;
  }
`;

// US States constant
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Add packages constant at the top level
const packages = [
  {
    id: 'free_trial',
    name: 'Enterprise Trial',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: '14-day free trial with all Enterprise features',
    features: [
      '$0.10 per successful booking',
      'Unlimited Email Notifications',
      'Customer Management',
      'Customer Profiles',
      'Customer Portal',
      'Unlimited Employee Management',
      'Mobile Support',
      'Deluxe',
      'Priority Support'
    ]
  },
  {
    id: 'solo',
    name: 'Solo',
    monthlyPrice: 20,
    yearlyPrice: 192,
    description: 'Perfect for individuals running their business on their own',
    features: [
      '$0.15 per successful booking',
      'Unlimited Email Notifications',
      'Customer Management',
      'Customer Profiles',
      'Customer Portal',
      '1 Employee (owner only)',
      'Mobile Support',
      'Deluxe',
      'Email Support'
    ]
  },
  {
    id: 'business',
    name: 'Business',
    monthlyPrice: 40,
    yearlyPrice: 384,
    description: 'Perfect for small businesses just getting started',
    features: [
      '$0.15 per successful booking',
      'Unlimited Email Notifications',
      'Customer Management',
      'Customer Profiles',
      'Customer Portal',
      '5 Employees',
      'Mobile Support',
      'Deluxe',
      'Email Support'
    ]
  },
  {
    id: 'business_plus',
    name: 'Business Plus',
    monthlyPrice: 80,
    yearlyPrice: 768,
    description: 'Ideal for growing businesses',
    features: [
      '$0.10 per successful booking',
      'Unlimited Email Notifications',
      'Customer Management',
      'Customer Profiles',
      'Customer Portal',
      '25 Employees',
      'Mobile Support',
      'Deluxe',
      'Email Support'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 500,
    yearlyPrice: 4800,
    description: 'For large organizations',
    features: [
      '$0.10 per successful booking',
      'Unlimited Email Notifications',
      'Customer Management',
      'Customer Profiles',
      'Customer Portal',
      'Unlimited Employee Management',
      'Mobile Support',
      'Deluxe',
      'Priority Support'
    ]
  }
];

const ReviewAndBillingStep = ({ 
  onNext, 
  onBack, 
  total = 0, 
  packageData, 
  businessData,
  billingCycle,
  interval,
  isYearly,
  initialBillingAddress,
  existingSubscription,
  onClearSubscription,
}) => {
  // Check if this is a free trial and the feature is enabled
  const isFreeTrial = featureFlags.FREE_TRIAL_ENABLED && (packageData?.id === 'free_trial' || (typeof packageData === 'string' && packageData === 'free_trial'));
  
  // Get package details including addons
  const packageDetails = typeof packageData === 'object' ? packageData : packages.find(pkg => pkg.id === packageData);
  const addons = packageDetails?.packageDetails?.addons || {};

  // Form state
  const [formData, setFormData] = useState(() => ({
    billingAddress: initialBillingAddress || businessData?.sameAsBillingAddress ? {
      firstName: businessData?.firstName || '',
      lastName: businessData?.lastName || '',
      street: businessData?.address?.street1 || '',
      city: businessData?.address?.city || '',
      state: businessData?.address?.state || '',
      zipCode: businessData?.address?.zipCode || ''
    } : {
      firstName: businessData?.firstName || '',
      lastName: businessData?.lastName || '',
      street: initialBillingAddress?.street || '',
      city: initialBillingAddress?.city || '',
      state: initialBillingAddress?.state || '',
      zipCode: initialBillingAddress?.zipCode || ''
    },
    vault: null,
    embeddedPayment: null,
  }));

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setupInFlight = useRef(false);
  const [fetchedPackage, setFetchedPackage] = useState(null);
  const [deluxeEnv, setDeluxeEnv] = useState(() => getDeluxeEnvironment());

  const hasExistingSubscription = hasConfirmedFirstPayment(existingSubscription);
  const subtotal = parseFloat(total) || 0;
  const tax = subtotal * 0.0825;
  const grandTotal = subtotal + tax;

  // Fetch package data from database
  const fetchPackageData = async (packageId) => {
    try {
      console.log('Fetching package data for:', packageId);
      
      // Fetch all packages and find the matching one by name
      const packages = await listPackages();
      
      console.log('All packages in database:', packages.map(pkg => ({ id: pkg.id, name: pkg.name })));
      
      // Find the matching package by name or ID
      let matchedPackage = null;
      
      // Map package IDs to expected names
      const packageNameMapping = {
        'solo': 'Solo',
        'business': 'Business',
        'business_plus': 'Business Plus',
        'enterprise': 'Enterprise'
      };
      
      const targetName = packageNameMapping[packageId];
      
      if (targetName) {
        // Look for package by name first, then by ID
        matchedPackage = packages.find(pkg =>
          pkg.slug === packageId ||
          pkg.name === targetName ||
          pkg.id === packageId
        );
      } else {
        // For other packages, try to match by ID or name
        matchedPackage = packages.find(pkg => 
          pkg.id === packageId ||
          pkg.name?.toLowerCase().includes(packageId.toLowerCase())
        );
      }
      
      if (matchedPackage) {
        console.log('Found package:', matchedPackage);
        console.log('Package limits from database:', matchedPackage.limits);
        console.log('Package features from database:', matchedPackage.features);
        setFetchedPackage(matchedPackage);
      } else {
        console.log('Package not found in database:', packageId);
        console.log('Available packages:', packages.map(pkg => pkg.name));
      }
    } catch (err) {
      console.error('Error fetching package data:', err);
    }
  };

  // Fetch package data when component mounts or packageData changes
  useEffect(() => {
    if (packageData) {
      const packageId = typeof packageData === 'string' ? packageData : packageData.id;
      if (packageId === 'free_trial' && featureFlags.FREE_TRIAL_ENABLED) {
        // For free trial, fetch Enterprise package features
        fetchPackageData('enterprise');
      } else if (packageId) {
        // For all other packages, fetch from database
        fetchPackageData(packageId);
      }
    }
  }, [packageData]);

  // Clear stored payment details once a subscription already exists
  useEffect(() => {
    if (existingSubscription) {
      setFormData(prev => ({
        ...prev,
        vault: null
      }));
    }
  }, [existingSubscription]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // PaymentForm will manage formatting/validation and inform us via onPaymentDetailsChange

  const validateForm = (paymentOverride) => {
    const { firstName, lastName, street, city, state, zipCode } = formData.billingAddress;
    if (!firstName || !lastName || !street || !city || !state || !zipCode) {
      setError('Please fill in all required billing address fields');
      return false;
    }

    // Basic ZIP code validation
    if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
      setError('Please enter a valid ZIP code');
      return false;
    }

    if (!hasExistingSubscription) {
      const paid = paymentOverride || formData.embeddedPayment;
      if (!paid?.paymentId) {
        setError('Complete the Deluxe payment form before continuing');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e, paymentOverride) => {
    e?.preventDefault?.();
    if (setupInFlight.current) return;
    setupInFlight.current = true;
    setError('');
    setLoading(true);
    sessionStorage.setItem('accountSetupInProgress', 'true');

    // Scroll to top when loading starts
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      if (!isFreeTrial && !hasExistingSubscription && !validateForm(paymentOverride)) {
        setupInFlight.current = false;
        setLoading(false);
        sessionStorage.removeItem('accountSetupInProgress');
        return;
      }

      // Shared calculations
      const finalBillingCycle = isYearly ? 'annually' : 'monthly';
      const subtotal = parseFloat(total) || 0;
      const tax = subtotal * 0.0825;
      const grandTotal = subtotal + tax;
      const customerName = `${businessData.firstName} ${businessData.lastName}`;
      const packageIdForSubmission = typeof packageData === 'string' ? packageData : packageData?.package || packageData?.id;
      const customerProfilePackageName = typeof packageData === 'string' 
        ? packages.find(pkg => pkg.id === packageData)?.name 
        : packageData?.packageDetails?.name || packageData?.name;

      // For free trial, skip payment processing (only when enabled)
      if (isFreeTrial) {
        const now = new Date();
        const trialEndDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days from now

        const trialPackageName = packages.find(pkg => pkg.id === 'free_trial')?.name || 'Enterprise Trial';

        console.log('Free trial package name resolution:', {
          trialPackageName,
          availablePackages: packages.map(pkg => ({ id: pkg.id, name: pkg.name }))
        });

        const setupData = {
          billingAddress: formData.billingAddress,
          businessData,
          package: 'free_trial',
          packageName: trialPackageName,
          total: 0,
          billingCycle: 'monthly',
          interval: 'month',
          isYearly: false,
          subscription: {
            id: `trial_${Date.now()}`,
            status: 'trial',
            amount: '0',
            billingCycle: 'monthly',
            startDate: now.toISOString(),
            nextBillingDate: trialEndDate.toISOString(),
            trialDays: 14
          }
        };

        console.log('Proceeding with free trial setup data:', setupData);
        await onNext(setupData);
        return;
      }

      if (hasExistingSubscription) {
        const setupData = {
          billingAddress: formData.billingAddress,
          businessData,
          packageData,
          package: packageIdForSubmission,
          packageName: customerProfilePackageName || 'Unknown Package',
          total: grandTotal,
          billingCycle: finalBillingCycle,
          interval,
          isYearly,
          subscription: existingSubscription
        };

        console.log('Reusing existing subscription for setup:', setupData);
        await onNext(setupData);
        return;
      }

      console.log('Package name resolution for subscription:', {
        packageData,
        packageDataType: typeof packageData,
        packageDataKeys: packageData ? Object.keys(packageData) : 'null',
        packageDataName: packageData?.name,
        packageDataId: packageData?.id,
        packageDetails: packageData?.packageDetails,
        customerProfilePackageName,
        availablePackages: packages.map(pkg => ({ id: pkg.id, name: pkg.name }))
      });

      const currentDate = new Date();
      const nextBillingDate = new Date(currentDate.getTime() + (isYearly ? 365 : 30) * 24 * 60 * 60 * 1000);
      const embeddedPayment = paymentOverride || formData.embeddedPayment;
      const vault = embeddedPayment?.paymentId
        ? {
            customerId: embeddedPayment.customerId,
            paymentMethodId: embeddedPayment.paymentMethodId,
            last4: embeddedPayment.last4,
          }
        : (formData.vault || {});

      const subscriptionMetadata = {
        businessId: null,
        packageId: packageIdForSubmission,
        packageName: customerProfilePackageName || 'Unknown Package',
        email: businessData.email,
        companyName: businessData.companyName,
        phone: businessData.businessPhone,
        customerName,
        billingAddress: formData.billingAddress,
        isTrialConversion: false,
        isRenewal: false,
        customerId: vault.customerId,
        paymentMethodId: vault.paymentMethodId,
        last4: vault.last4,
        deluxeEnvironment: deluxeEnv,
      };

      const subscriptionName = customerProfilePackageName || 'Unknown Package';

      const subscriptionResponse = await createSubscription({
        name: subscriptionName,
        amount: grandTotal.toFixed(2),
        billingCycle: finalBillingCycle === 'yearly' ? 'annually' : finalBillingCycle,
        vault,
        embeddedPayment,
        metadata: subscriptionMetadata,
        startDate: currentDate.toISOString(),
        nextBillingDate: nextBillingDate.toISOString()
      });

      console.log('Subscription Response:', subscriptionResponse);

      if (subscriptionResponse.success && hasConfirmedFirstPayment({
        ...subscriptionResponse.subscription,
        firstPayment: subscriptionResponse.firstPayment,
      })) {
        const setupData = {
          billingAddress: formData.billingAddress,
          businessData,
          packageData,
          package: packageIdForSubmission,
          packageName: customerProfilePackageName || 'Unknown Package',
          total: grandTotal,
          billingCycle: finalBillingCycle,
          interval,
          isYearly,
          subscription: {
            ...subscriptionResponse.subscription,
            firstPayment: subscriptionResponse.firstPayment,
            deluxeCustomerId: vault.customerId || embeddedPayment?.customerId,
            deluxePaymentMethodId: vault.paymentMethodId || embeddedPayment?.paymentMethodId,
            paymentMethodLast4: vault.last4 || embeddedPayment?.last4 || subscriptionResponse.subscription?.paymentMethodLast4,
          }
        };

        console.log('Proceeding with setup data:', setupData);
        await onNext(setupData);
      } else {
        setError(
          subscriptionResponse.success
            ? 'Payment was not confirmed. No account was created.'
            : (subscriptionResponse.message || 'Subscription creation failed. Please try again.')
        );
      }
    } catch (err) {
      sessionStorage.removeItem('accountSetupInProgress');
      console.error('Subscription creation error:', err);
      const detailsCode = err?.details?.code || err?.code;
      const sandboxActive = isDeluxeSandbox();
      if (detailsCode === 'CARD_DECLINED') {
        setError('Your card was declined. No account was created.');
      } else if (
        typeof err?.message === 'string' &&
        (err.message.includes('not configured') || err.message.toLowerCase().includes('authentication'))
      ) {
        setError(
          sandboxActive
            ? 'Deluxe sandbox credentials are invalid. Save the sandbox Access Token and Signature Key in admin App Settings, then try again.'
            : 'Deluxe production credentials are invalid. Save the Access Token and Signature Key in admin App Settings, then try again.'
        );
      } else if (
        typeof err?.message === 'string' && err.message.toLowerCase().includes('declined')
      ) {
        setError('Your card was declined. No account was created.');
      } else if (
        err?.details?.code === 'PROFILE_VALIDATION_FAILED' ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('validate your card'))
      ) {
        setError('We could not validate your card. Please use a real credit or debit card and try again.');
      } else {
        setError(err.message || 'An error occurred while setting up your subscription. Please try again.');
      }
    } finally {
      setupInFlight.current = false;
      setLoading(false);
    }
  };
  return (
    <AccountSetupStepShell>
      <ScrollToTop />
      <AccountSetupStepContainer>
        <AccountStepper currentStep={3} />
        
        <ContentGrid>
          <FormContainer onSubmit={handleSubmit}>
            {error && (
              <div className="rbs-alert rbs-alert-error">{error}</div>
            )}

            <ReviewSection>
              <div className="rbs-summary">
                <div className="rbs-summary-header rbs-summary-header--center">Review Your Information</div>
                <hr className="rbs-divider" />

              <div className="rbs-card">
                <div className="rbs-card-header">Business Information</div>
                  <ReviewItem>
                    <span>Business Name</span>
                    <span>{businessData?.companyName || 'N/A'}</span>
                  </ReviewItem>
                  <ReviewItem>
                    <span>Email</span>
                    <span>{businessData?.email || 'N/A'}</span>
                  </ReviewItem>
                  <ReviewItem>
                    <span>Business Phone</span>
                    <span>{businessData?.businessPhone || 'N/A'}</span>
                  </ReviewItem>
                  {businessData?.personalPhone && (
                    <ReviewItem>
                      <span>Mobile Phone</span>
                      <span>{businessData.personalPhone}</span>
                    </ReviewItem>
                  )}
                  <ReviewItem>
                    <span>Contact Name</span>
                    <span>{`${businessData?.firstName || ''} ${businessData?.lastName || ''}`}</span>
                  </ReviewItem>
                </div>

              <div className="rbs-card">
                <div className="rbs-card-header">Selected Package</div>
                  <ReviewItem>
                    <span>Package Name</span>
                    <span>
                      {isFreeTrial 
                        ? 'Enterprise Trial' 
                        : (packages.find(pkg => pkg.id === (packageDetails?.id || packageData))?.name || packageDetails?.name || 'N/A')}
                    </span>
                  </ReviewItem>
                  <ReviewItem>
                    <span>Price</span>
                    <span>
                      {isFreeTrial 
                        ? 'Free for 14 days'
                        : `$${isYearly 
                            ? (packages.find(pkg => pkg.id === (packageDetails?.id || packageData))?.yearlyPrice || packageDetails?.yearlyPrice || 0).toFixed(2)
                            : (packages.find(pkg => pkg.id === (packageDetails?.id || packageData))?.monthlyPrice || packageDetails?.monthlyPrice || 0).toFixed(2)
                          }/${isYearly ? 'year' : 'month'}`
                      }
                    </span>
                  </ReviewItem>
                  {!isFreeTrial && packageDetails?.id === 'business_plus' && Object.entries(addons).length > 0 && (
                    <>
                      <hr className="rbs-divider" />
                      <div className="rbs-subtitle">Selected Add-ons</div>
                      {Object.entries(addons).map(([key, quantity]) => (
                        quantity > 0 && (
                          <ReviewItem key={key}>
                            <span>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            <span>Qty: {quantity}</span>
                          </ReviewItem>
                        )
                      ))}
                    </>
                  )}
                  
                  {!isFreeTrial && (packageDetails?.packageDetails?.limits || packageDetails?.limits) && (
                    <>
                      <hr className="rbs-divider" />
                      <div className="rbs-subtitle">Total Limits</div>
                      {(() => {
                        // Define correct limits for each package
                        const correctLimits = {
                          solo: { employees: 1, appointments: -1 },
                          business: { employees: 5, appointments: -1 },
                          business_plus: { employees: 25, appointments: -1 },
                          enterprise: { employees: -1, appointments: -1 }
                        };
                        
                        const packageId = packageDetails.id;
                        const dbLimits = packageDetails.packageDetails?.limits || packageDetails.limits;
                        const correctLimit = correctLimits[packageId];
                        
                        // Use correct limits if database limits are wrong
                        const limitsToUse = correctLimit && (
                          dbLimits.employees !== correctLimit.employees
                        ) ? correctLimit : dbLimits;
                        
                        console.log('Rendering limits for package:', {
                          packageId,
                          dbLimits,
                          correctLimit,
                          limitsToUse,
                          hasLimits: !!packageDetails?.packageDetails?.limits
                        });
                        
                        return (
                          <>
                            <ReviewItem>
                              <span>Total Employees</span>
                              <span>{limitsToUse.employees === -1 ? 'Unlimited' : limitsToUse.employees}</span>
                            </ReviewItem>
                            <ReviewItem>
                              <span>Appointments</span>
                              <span>{limitsToUse.appointments === -1 ? 'Unlimited' : limitsToUse.appointments}</span>
                            </ReviewItem>
                          </>
                        );
                      })()}
                    </>
                  )}
                  {(isFreeTrial || fetchedPackage) && (
                    <>
                      <hr className="rbs-divider" />
                      <div className="rbs-subtitle">Included Features</div>
                      {(() => {
                        // Use database features if available, otherwise fallback to hardcoded
                        const packageToUse = fetchedPackage || packages.find(pkg => pkg.id === (isFreeTrial ? 'free_trial' : packageData));
                        const features = packageToUse?.features;
                        
                        console.log('Rendering features:', {
                          packageToUse,
                          features,
                          isFreeTrial,
                          packageData
                        });
                        
                        if (features) {
                          if (Array.isArray(features)) {
                            // New array structure
                            console.log('Using array features from database:', features);
                            return features.map((feature, index) => (
                              <ReviewItem key={index}>
                                <span>{feature}</span>
                                <span className="rbs-check">✓</span>
                              </ReviewItem>
                            ));
                          } else if (typeof features === 'object') {
                            // Old object structure - convert to display format
                            console.log('Using object features from database:', features);
                            return Object.entries(features)
                              .filter(([key, enabled]) => enabled)
                              .map(([feature, enabled], index) => (
                                <ReviewItem key={index}>
                                  <span>{feature.split('_').map(word => 
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                  ).join(' ')}</span>
                                  <span className="rbs-check">✓</span>
                                </ReviewItem>
                              ));
                          }
                        }
                        
                        // Fallback to hardcoded features if database fetch fails
                        console.log('Using fallback hardcoded features');
                        const fallbackPackage = packages.find(pkg => pkg.id === (isFreeTrial ? 'free_trial' : packageData));
                        return fallbackPackage?.features?.map((feature, index) => (
                          <ReviewItem key={index}>
                            <span>{feature}</span>
                            <span className="rbs-check">✓</span>
                          </ReviewItem>
                        )) || [];
                      })()}
                    </>
                  )}
                </div>
              </div>
            </ReviewSection>

            <div className="rbs-summary" style={{ marginTop: 16 }}>
              <div className="rbs-summary-header rbs-summary-header--center">Billing Address</div>
              <hr className="rbs-divider" />
              <div className="rbs-form-grid">
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-first-name">First Name</label>
                  <input id="rbs-first-name" className="rbs-input" name="billingAddress.firstName" value={formData.billingAddress.firstName} onChange={handleChange} required />
                </div>
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-last-name">Last Name</label>
                  <input id="rbs-last-name" className="rbs-input" name="billingAddress.lastName" value={formData.billingAddress.lastName} onChange={handleChange} required />
                </div>
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-street">Street</label>
                  <input id="rbs-street" className="rbs-input" name="billingAddress.street" value={formData.billingAddress.street} onChange={handleChange} required />
                </div>
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-city">City</label>
                  <input id="rbs-city" className="rbs-input" name="billingAddress.city" value={formData.billingAddress.city} onChange={handleChange} required />
                </div>
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-state">State</label>
                  <select id="rbs-state" className="rbs-input" name="billingAddress.state" value={formData.billingAddress.state} onChange={handleChange} required>
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="rbs-field">
                  <label className="rbs-label" htmlFor="rbs-zip">ZIP Code</label>
                  <input id="rbs-zip" className="rbs-input" name="billingAddress.zipCode" value={formData.billingAddress.zipCode} onChange={handleChange} placeholder="12345" maxLength={10} required />
                </div>
              </div>
              <p className="rbs-summary-note" style={{ marginTop: 16 }}>
                {isFreeTrial ? 
                  'Your free trial will start immediately after completing the setup' :
                  'Your payment will be processed securely before completing the account setup'}
              </p>
            </div>

            {!isFreeTrial && (
              <div className="rbs-summary" style={{ marginTop: 16 }}>
                <div className="rbs-summary-header rbs-summary-header--center">Payment Information</div>
                <hr className="rbs-divider" />
                <div className="rbs-info-banner">Your payment information is encrypted and processed securely through Deluxe.</div>

                <DeluxeEnvironmentToggle
                  onEnvironmentChange={(nextEnv) => {
                    setDeluxeEnv(nextEnv);
                    onClearSubscription?.();
                  }}
                />

                {hasExistingSubscription && (
                  <div className="rbs-info-banner" style={{ marginTop: 12 }}>
                    We've already processed your payment. Review your details and click Complete Setup to finish creating your account. If you need to update your payment method, please contact our support team so we can safely reset your subscription.
                  </div>
                )}

                <div style={{ marginTop: 8 }}>
                  {formData.embeddedPayment?.paymentId ? (
                    <div className="rbs-info-banner">
                      Payment received{formData.embeddedPayment.last4 ? ` for the card ending in ${formData.embeddedPayment.last4}` : ''}. Setting up your account…
                    </div>
                  ) : (
                    <DeluxeSubscriptionCheckout
                      amount={grandTotal}
                      billingCycle={isYearly ? 'annually' : 'monthly'}
                      description={`${packageDetails?.name || packageDetails?.packageDetails?.name || 'Neat Schedule'} subscription`}
                      environment={deluxeEnv}
                      disabled={loading || hasExistingSubscription}
                      customer={{
                        firstName: formData.billingAddress.firstName,
                        lastName: formData.billingAddress.lastName,
                        email: businessData?.email,
                        billingAddress: {
                          address: formData.billingAddress.street,
                          city: formData.billingAddress.city,
                          state: formData.billingAddress.state,
                          zipCode: formData.billingAddress.zipCode,
                        },
                      }}
                      onPaid={(payment) => {
                        setFormData((prev) => ({
                          ...prev,
                          embeddedPayment: payment,
                          vault: {
                            customerId: payment.customerId,
                            paymentMethodId: payment.paymentMethodId,
                            last4: payment.last4,
                          },
                        }));
                        setError('');
                        handleSubmit(null, payment);
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            <AccountSetupButtonRow>
              <AccountSetupBackButton type="button" onClick={onBack} disabled={loading}>
                Back
              </AccountSetupBackButton>
              <AccountSetupNextButton type="submit" disabled={loading}>
                {loading && <span className="rbs-inline-spinner" aria-hidden> </span>}
                {loading ? 'One moment…' : isFreeTrial ? 'Start Free Trial' : 'Complete Setup'}
              </AccountSetupNextButton>
            </AccountSetupButtonRow>

            {loading && <AccountSetupLoadingOverlay isFreeTrial={isFreeTrial} />}
          </FormContainer>

            <div className="rbs-summary" style={{ gridArea: 'summary' }}>
            <div className="rbs-summary-header rbs-summary-header--center">Order Summary</div>
            <div style={{ 
              textAlign: 'center', 
              fontSize: '0.875rem', 
              color: theme.colors.primary.main, 
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 'bold' }}>✓</span> No hidden fees - flat rate pricing
            </div>
            <hr className="rbs-divider" />
            <div className="rbs-summary-row">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="rbs-summary-row">
              <span>Tax (8.25%)</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <hr className="rbs-divider" />
            <div className="rbs-summary-row rbs-summary-total">
              <span>Total</span>
              <span>${grandTotal.toFixed(2)}</span>
            </div>
            <div className="rbs-summary-note">Your payment will be processed securely through our payment provider</div>
            {isFreeTrial && (
              <div className="rbs-summary-trial">
                Your 14-day free trial includes all features of the Enterprise package. No payment required during trial period.
              </div>
            )}
          </div>
        </ContentGrid>
      </AccountSetupStepContainer>
    </AccountSetupStepShell>
  );
};

export default ReviewAndBillingStep; 